import { useState, useEffect, useCallback } from 'react';

export interface UseAsyncDataOptions<T> {
  /** Async function that fetches the data */
  fetchFn: () => Promise<T>;
  /** Dependencies array for useEffect (triggers refetch when changed) */
  dependencies?: unknown[];
  /** Whether the fetch should be enabled (default: true) */
  enabled?: boolean;
  /** Callback fired on successful fetch */
  onSuccess?: (data: T) => void;
  /** Callback fired on error */
  onError?: (error: Error) => void;
}

export interface UseAsyncDataResult<T> {
  /** The fetched data (null if not yet loaded or error) */
  data: T | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state (null if no error) */
  error: Error | null;
  /** Manually trigger a refetch */
  refetch: () => Promise<void>;
}

/**
 * Generic hook for async data fetching with loading and error states
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useAsyncData({
 *   fetchFn: () => api.getUsers(),
 * });
 * ```
 *
 * @example With dependencies (refetch when filters change)
 * ```tsx
 * const { data, isLoading, error } = useAsyncData({
 *   fetchFn: () => api.getUsers(filters),
 *   dependencies: [filters],
 * });
 * ```
 */
export function useAsyncData<T>({
  fetchFn,
  dependencies = [],
  enabled = true,
  onSuccess,
  onError,
}: UseAsyncDataOptions<T>): UseAsyncDataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const execute = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await fetchFn();
      setData(result);
      onSuccess?.(result);
    } catch (err) {
      const error = err as Error;
      setError(error);
      setData(null);
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, onSuccess, onError, ...dependencies]);

  useEffect(() => {
    void execute();
  }, [execute]);

  return { data, isLoading, error, refetch: execute };
}


export interface UseAsyncMutationOptions<T, P> {
  /** Async function that performs the mutation */
  mutationFn: (params: P) => Promise<T>;
  /** Callback fired on successful mutation */
  onSuccess?: (data: T) => void;
  /** Callback fired on error */
  onError?: (error: Error) => void;
}

export interface UseAsyncMutationResult<T, P> {
  /** Execute the mutation with given params */
  mutate: (params: P) => Promise<T | null>;
  /** Result data from the mutation (null if not yet executed or error) */
  data: T | null;
  /** Loading state (true while mutation is in progress) */
  isLoading: boolean;
  /** Error state (null if no error) */
  error: Error | null;
  /** Reset the mutation state */
  reset: () => void;
}

/**
 * Generic hook for async mutations (POST/PUT/DELETE) with loading and error states
 *
 * @example
 * ```tsx
 * const { mutate, isLoading, error } = useAsyncMutation({
 *   mutationFn: (data: UserFormData) => api.createUser(data),
 *   onSuccess: () => toast.success('User created'),
 * });
 *
 * // Later...
 * await mutate(formData);
 * ```
 */
export function useAsyncMutation<T, P = void>({
  mutationFn,
  onSuccess,
  onError,
}: UseAsyncMutationOptions<T, P>): UseAsyncMutationResult<T, P> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (params: P): Promise<T | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await mutationFn(params);
      setData(result);
      onSuccess?.(result);
      return result;
    } catch (err) {
      const error = err as Error;
      setError(error);
      setData(null);
      onError?.(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setData(null);
    setError(null);
    setIsLoading(false);
  };

  return { mutate, data, isLoading, error, reset };
}
